import type {ItemIdentifier} from '@github-ui/issue-viewer/Types'
import {SingleSignOnBanner} from '@github-ui/single-sign-on-banner'
import {useRef} from 'react'
import {graphql, useFragment} from 'react-relay'

import {useActiveSearchQuery, useQueryEditState} from '../../contexts/QueryContext'
import {useSsoOrgDetection} from '../../hooks/use-sso-org-detection'
import type {LoadSearchQuery} from '../../pages/shared'
import type {IssueSearchType} from '../../types/issue-search-type'
import NewViewExperience from '../list/NewViewExperience'
import type {SearchCurrentViewFragment$key} from './__generated__/SearchCurrentViewFragment.graphql'
import type {SearchRepositoryFragment$key} from './__generated__/SearchRepositoryFragment.graphql'
import type {SearchRootFragment$key} from './__generated__/SearchRootFragment.graphql'
import styles from './Search.module.css'
import {SearchBar} from './SearchBar'
import {SearchList} from './SearchList'

type SearchProps = {
  itemIdentifier: ItemIdentifier | undefined
  currentViewKey: SearchCurrentViewFragment$key
  currentRepository: SearchRepositoryFragment$key | null
  search: SearchRootFragment$key
  loadSearchQuery?: LoadSearchQuery
  showSsoBanner?: boolean
  queryFromCustomView?: string | null
  onSidePanelNavigate?: (issue: ItemIdentifier) => void
  trackIssueNavigation: (position?: number) => void
  updateIssueSearchType?: (issueSearchType: string | null | undefined) => void
  onListItemsChange?: (items: ItemIdentifier[]) => void
  searchType?: IssueSearchType
}

export function Search({
  itemIdentifier,
  currentViewKey,
  currentRepository,
  search,
  loadSearchQuery,
  queryFromCustomView,
  onSidePanelNavigate,
  showSsoBanner,
  trackIssueNavigation,
  updateIssueSearchType,
  onListItemsChange,
  searchType,
}: SearchProps) {
  const activeSearchQuery = useActiveSearchQuery()
  const {isNewView} = useQueryEditState()
  const {orgNames, allSsoOrgs, filterDetectedSsoOrgs, onSsoOrgsFromFilter} = useSsoOrgDetection(activeSearchQuery ?? '')

  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment SearchRootFragment on Searchable
      @argumentDefinitions(
        query: {type: "String!"}
        first: {type: "Int"}
        labelPageSize: {type: "Int!"}
        skip: {type: "Int", defaultValue: null}
        fetchRepository: {type: "Boolean!"}
        type: {type: "SearchType", defaultValue: ISSUE_ADVANCED}
      ) {
        ...SearchList
          @arguments(
            query: $query
            first: $first
            labelPageSize: $labelPageSize
            fetchRepository: $fetchRepository
            skip: $skip
            type: $type
          )
      }
    `,
    search,
  )

  const repoData = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment SearchRepositoryFragment on Repository {
        ...SearchListRepo
        ...SearchBarRepo
      }
    `,
    currentRepository,
  )

  const currentViewData = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment SearchCurrentViewFragment on Shortcutable {
        ...SearchBarCurrentViewFragment
      }
    `,
    currentViewKey,
  )

  const listRef = useRef<HTMLUListElement | undefined>(undefined)

  return (
    <div className={styles.SearchContainer}>
      <SearchBar
        currentViewKey={currentViewData}
        currentRepository={repoData ?? null}
        queryFromCustomView={queryFromCustomView}
        onSsoOrgsFromFilter={onSsoOrgsFromFilter}
        ssoOrgNames={orgNames}
      />
      {isNewView && !activeSearchQuery ? (
        <NewViewExperience />
      ) : (
        <>
          <SingleSignOnBanner protectedOrgs={showSsoBanner ? allSsoOrgs : filterDetectedSsoOrgs} />
          <SearchList
            itemIdentifier={itemIdentifier}
            search={data}
            repository={repoData ?? null}
            loadSearchQuery={loadSearchQuery}
            query={activeSearchQuery}
            queryFromCustomView={queryFromCustomView}
            listRef={listRef}
            onSidePanelNavigate={onSidePanelNavigate}
            onIssueNavigate={trackIssueNavigation}
            updateIssueSearchType={updateIssueSearchType}
            onListItemsChange={onListItemsChange}
            searchType={searchType}
            hasSsoOrgs={filterDetectedSsoOrgs.length > 0}
          />
        </>
      )}
    </div>
  )
}
